import { useEffect, useRef, useState } from 'react'

import { useRuleActionNavigation } from '../../../shared/hooks/useRuleActionNavigation'
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlags,
} from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { AnimatePresence } from 'framer-motion'

import {
  Box,
  FeedItem as DarkFeedItem,
  Flex,
  OperatorAvatar,
  Text,
} from '@hivewatch/dark'
import type { StatusPillVariant } from '@hivewatch/dark'

import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentActionType } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { formatFullName } from '@/utils/formats'

import { CannedIncidentActionType } from '../../../shared/types/feedTypes'
import { MixpanelDataIProps } from '../../../shared/types/types'
import {
  formatFeedTimestamp,
  getActivityTimeline,
} from '../../../shared/utils/feedUtils'
import { ActionIcon } from './ActionIcon'
import { AudioPlayer } from './AudioPlayer'
import { FeedItemsSkeleton } from './FeedItemsSkeleton'
import { ActionHeader } from './IncidentAction'
import { IncidentDetails } from './IncidentDetails'
import { IncidentNoteMedia } from './IncidentNoteMedia'
import { ResolutionContentItem } from './ResolutionContentItem'
import { ShowMore } from './ShowMore'

const getActionStatusPill = (
  actionType: string,
  t: TFunction
): { statusVariant?: StatusPillVariant; statusLabel?: string } => {
  switch (actionType) {
    case IncidentActionType.AcknowledgementByOperator:
      return {
        statusVariant: 'in-progress',
        statusLabel: t('incidents:feed_status_in_progress'),
      }
    case IncidentActionType.ReleaseByOperator:
      return {
        statusVariant: 'incomplete',
        statusLabel: t('incidents:feed_status_active'),
      }
    case IncidentActionType.Resolved:
      return {
        statusVariant: 'complete',
        statusLabel: t('incidents:feed_status_resolved'),
      }
    default:
      return {}
  }
}

// Create a fingerprint from item IDs to detect actual data changes
const getTimelineFingerprint = (incident: IncidentQuery['incident']) => {
  if (!incident) return null

  const actionIds = incident.actions?.map((a) => a.id).join(',') ?? ''
  const noteIds =
    incident.notes?.edges
      ?.map((e) => {
        const mediaIds = e?.node?.media?.map((m) => m?.id).join('+') ?? ''
        return `${e?.node?.id}:${mediaIds}`
      })
      .join(',') ?? ''
  const audioIds =
    incident.audioMessages?.edges?.map((e) => e?.node?.id).join(',') ?? ''
  const eventCount = incident.events?.length ?? 0

  return `${actionIds}|${noteIds}|${audioIds}|${eventCount}`
}

interface TimelineFeedIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
  isLoading?: boolean
  onClose?: () => void
}

export const TimelineFeed = ({
  incident,
  mixpanelData,
  isLoading = false,
  onClose,
}: TimelineFeedIProps) => {
  const { t } = useTranslation()
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const colorScheme = useLightMode()
  const [items, setItems] = useState(null)
  const me = useAppSelector(selectMe)
  const {
    attachMediaToIncidentNote: isAttachMediaToIncidentNoteEnabled,
    salesDatabaseReset: isSalesEnvironment,
  } = useFeatureFlags()

  const handleRuleActionClick = useRuleActionNavigation({
    mixpanelData,
    onClose,
    items,
  })

  const prevFingerprintRef = useRef<string | null>(null)

  useEffect(() => {
    const fingerprint = getTimelineFingerprint(incident)

    if (fingerprint && fingerprint === prevFingerprintRef.current) {
      return
    }
    prevFingerprintRef.current = fingerprint

    const actions = incident?.actions
    const events = incident?.events
    const audioMessages = incident?.audioMessages?.edges?.map((a) => a?.node)
    const notes = incident?.notes.edges?.map((a) => a?.node)

    if (actions && audioMessages && notes && events) {
      const activityTimeLine = getActivityTimeline(
        actions,
        events,
        audioMessages,
        notes,
        me,
        isSalesEnvironment
      )
      setItems(activityTimeLine)
    }
  }, [incident, me, isSalesEnvironment])

  const trackExpandDescriptionClick = (eventType: 'more' | 'less'): void => {
    let eventName: string
    switch (eventType) {
      case 'more':
        eventName = 'Show More Button Pressed (Feed)'
        break
      case 'less':
        eventName = 'Show Less Button Pressed (Feed)'
        break
    }
    trackEvent(eventName, mixpanelData)
  }

  return (
    <Box
      borderTop='1px solid'
      borderTopColor={
        colorScheme === 'dark' ? 'border.subtle' : 'light.border.subtle'
      }
      p='24px'
    >
      {isLoading || !items ? (
        <FeedItemsSkeleton />
      ) : (
        <AnimatePresence>
          <IncidentDetails incident={incident} mixpanelData={mixpanelData} />

          {items?.length > 0 && (
            <Text
              color={
                colorScheme === 'dark' ? 'text.secondary' : 'light.text.primary'
              }
              fontFamily="'Soehne Kraftig', sans-serif"
              fontSize='16px'
              // Kraftig registers a single 500-weight face; a heavier value
              // triggers synthetic bolding on top of the semibold face.
              fontWeight={500}
              lineHeight='normal'
              mb='10px'
              mt='24px'
              pt='24px'
            >
              {t('incidents:incident_drawer_timeline_feed')}
            </Text>
          )}

          <Flex direction='column' gap='4px'>
            {items.map((a, i: number) => {
              const actionType = a?.type || a?.nonActionType
              const { statusVariant, statusLabel } = getActionStatusPill(
                actionType,
                t
              )
              // An emailed notification is sent *by* the AI Operator to the
              // named recipient, so `creator` can't identify the actor. Legacy
              // showed the AI avatar on these rows, and the header still reads
              // "AI Operator sent an email notification".
              const isAiEmail = actionType === CannedIncidentActionType.Email
              const actorName = formatFullName(a?.creator ?? {})
              // System rows (e.g. NonActionItem.SystemAction) have no creator.
              // Legacy rendered no avatar at all for them; passing one anyway
              // would render UserAvatar's "N/A" placeholder and read as a
              // misattributed actor.
              const hasActor = isAiEmail || actorName.trim().length > 0
              return (
                <DarkFeedItem
                  avatar={
                    hasActor ? (
                      <OperatorAvatar
                        colorScheme={colorScheme}
                        isAiOperator={isAiEmail}
                        name={actorName}
                      />
                    ) : undefined
                  }
                  colorScheme={colorScheme}
                  icon={ActionIcon(actionType, colorScheme, {
                    priority: a?.history?.newValue,
                  })}
                  key={a?.id ?? i}
                  message={ActionHeader({
                    actionType,
                    username: `${a?.creator?.firstName} ${a?.creator?.lastName}`,
                    roleName: a?.creator?.roleName,
                    systemMessage: a?.systemMessage,
                    technicianName: `${a?.deviceRepairOrder?.technician?.firstName} ${a?.deviceRepairOrder?.technician?.lastName}`,
                    history: a?.history,
                    isRuleAction: a?.isRuleAction,
                    emailRecipientCount: a?.emailRecipients?.length,
                    onRuleClick: a?.ruleId
                      ? () => handleRuleActionClick(a.ruleId)
                      : undefined,
                    shouldShowStatusTag: false,
                  })}
                  statusLabel={statusLabel}
                  statusVariant={statusVariant}
                  timestamp={formatFeedTimestamp(a.createdAt)}
                >
                  {a?.audioClip?.url && (
                    <AudioPlayer
                      colorScheme={colorScheme}
                      url={a.audioClip.url}
                    />
                  )}
                  {/* Resolution actions use resolutionContent.segments (supports TEXT and EVENT_REF types).
                        Notes use message field and render via ShowMore for plain text display. */}
                  {a?.resolutionContent && isTraceLogsEnabled ? (
                    <ResolutionContentItem
                      content={a.resolutionContent}
                      handleShowLess={() => trackExpandDescriptionClick('less')}
                      handleShowMore={() => trackExpandDescriptionClick('more')}
                    />
                  ) : a?.message ? (
                    <ShowMore
                      handleShowLess={() => trackExpandDescriptionClick('less')}
                      handleShowMore={() => trackExpandDescriptionClick('more')}
                      message={a.message}
                    />
                  ) : null}
                  {isAttachMediaToIncidentNoteEnabled &&
                    a?.media?.length > 0 && (
                      <IncidentNoteMedia files={a?.media} />
                    )}
                </DarkFeedItem>
              )
            })}
          </Flex>
        </AnimatePresence>
      )}
    </Box>
  )
}
